import { useState } from 'react';
import { X, Landmark } from 'lucide-react';
import { T } from '../theme';

const INPUT = {
  width: '100%', boxSizing: 'border-box',
  border: '1.5px solid #E5E7EB', borderRadius: 10, padding: '10px 12px',
  fontSize: 15, fontFamily: T.fontBody, outline: 'none', color: '#111',
};

const SEG_BTN = (active, left, right) => ({
  flex: 1, padding: '9px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer',
  border: '1.5px solid #E5E7EB', background: active ? T.tealDeep : '#fff',
  color: active ? '#fff' : '#374151',
  borderRadius: left ? '8px 0 0 8px' : right ? '0 8px 8px 0' : 0,
  borderRight: left ? 'none' : undefined,
});

export default function CreateAccountModal({ onClose, onSave, existingAccounts = [] }) {
  const [name, setName]                   = useState('');
  const [reportType, setReportType]       = useState('bank');
  const [type, setType]                   = useState('personal');
  const [openingBalance, setOpeningBalance] = useState('');
  const [openingDate, setOpeningDate]     = useState('');
  const [error, setError]                 = useState('');
  const [saving, setSaving]               = useState(false);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('Account name is required.'); return; }
    if (trimmed.includes('/')) { setError('Account name cannot contain "/".'); return; }
    if (existingAccounts.some(a => a.name === trimmed)) { setError('An account with this name already exists.'); return; }
    const obValue = openingBalance.trim() !== '' ? parseFloat(openingBalance) : undefined;
    if (openingBalance.trim() !== '' && isNaN(obValue)) { setError('Opening balance must be a number.'); return; }
    setSaving(true);
    try {
      await onSave({
        name: trimmed,
        reportType,
        type,
        ...(obValue !== undefined ? { openingBalance: obValue } : {}),
        ...(openingDate ? { openingBalanceDate: openingDate } : {}),
      });
      onClose();
    } catch (e) {
      setError('Failed to create account. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16,
    }}>
      <div style={{
        background: '#fff', borderRadius: 20, padding: '24px 20px',
        width: '100%', maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Landmark size={18} color="#15803d" />
            </div>
            <span style={{ fontSize: 17, fontWeight: 800, color: '#111' }}>New Account</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="#6B7280" />
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>ACCOUNT NAME</div>
            <input
              style={INPUT}
              placeholder="e.g. Chase Checking, Amex Platinum"
              value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              autoFocus
              maxLength={80}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>ACCOUNT KIND</div>
            <div style={{ display: 'flex' }}>
              <button style={SEG_BTN(reportType === 'bank', true, false)} onClick={() => setReportType('bank')}>
                Bank
              </button>
              <button style={SEG_BTN(reportType === 'credit_card', false, false)} onClick={() => setReportType('credit_card')}>
                Credit Card
              </button>
              <button style={SEG_BTN(reportType === 'brokerage', false, true)} onClick={() => setReportType('brokerage')}>
                Brokerage
              </button>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>OWNER TYPE</div>
            <div style={{ display: 'flex' }}>
              <button style={SEG_BTN(type === 'personal', true, false)} onClick={() => setType('personal')}>
                Personal
              </button>
              <button style={SEG_BTN(type === 'business', false, true)} onClick={() => setType('business')}>
                Business
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5 }}>
              Sets the default classification for imported transactions. Individual items can always be reclassified.
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', marginBottom: 6 }}>OPENING BALANCE <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(optional)</span></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="number"
                style={{ ...INPUT, width: '50%' }}
                placeholder="0.00"
                value={openingBalance}
                onChange={e => { setOpeningBalance(e.target.value); setError(''); }}
              />
              <input
                type="date"
                style={{ ...INPUT, width: '50%', color: openingDate ? '#111' : '#9CA3AF' }}
                value={openingDate}
                onChange={e => setOpeningDate(e.target.value)}
              />
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 5 }}>
              Balance on the date shown. Leave blank to estimate from transaction history.
            </div>
          </div>

          {error && <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>{error}</div>}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              background: saving ? '#9CA3AF' : T.gradTeal, border: 'none', borderRadius: 12,
              padding: '13px 0', fontSize: 15, fontWeight: 800, color: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer', width: '100%', marginTop: 4,
            }}
          >
            {saving ? 'Creating…' : 'Create Account'}
          </button>
        </div>
      </div>
    </div>
  );
}
